import { set } from 'lodash'
import { Observable, of } from 'rxjs'
import { filter, switchMap, catchError } from 'rxjs/operators'

import { User, Cache, DataStoreOptions, SpecimenResource, Item } from '@skinsoft/types'
import { Proxy } from '@skinsoft/network'
import { AuthService } from '@skinsoft/auth'
import { DataStore } from '@skinsoft/data-store'
import { CREATE_PATTERNS, CreatePattern } from '@skinsoft/const'

interface EntityDataStoreOptions extends DataStoreOptions {
    userScope?: boolean;
}

export abstract class EntityDataStore<T extends { id: string } = any, U = any, S extends { id: string } = T> extends DataStore<T, S> {
    public readonly featuredStates: U[] = [];
    public readonly historyStates: U[] = [];
    public readonly sortFields: string[] = [];
    public readonly entityName: string;

    constructor(
        entityName: string,
        typesForSearch?: string[],
        proxy?: Proxy,
        fragmentParser = response => (response._embedded ? response._embedded[entityName] : []),
        createPattern: CreatePattern = CREATE_PATTERNS.CREATE_THEN_UPDATE,
    ) {
        super(
            `/${entityName}`,
            typesForSearch,
            fragmentParser, // for getAll
            proxy,
            createPattern,
        );
        this.entityName = entityName;
    }

    public fetchAll(options: EntityDataStoreOptions = {}): Observable<T[]> {
        const { userScope, ...otherOptions } = options;

        if (!userScope) {
            return super.fetchAll(otherOptions);
        }

        return AuthService.getInstance().user$.pipe(
            filter(u => u !== null && u.user != null),
            switchMap(userAuthResult =>
                super.fetchAll(set(otherOptions, 'query.affectedUser', (userAuthResult!.user as User).id)),
            ),
        );
    }

    public fetchIfHasAccess(content: string) {
        return this.fetchOne(content).pipe(
            catchError((err) => err.status === 403 ? of(null) : err)
        )
    }
}
